import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  CalendarPlus, 
  Ticket, 
  DollarSign, 
  BarChart3, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  FileText,
  Plus,
  ArrowRight,
  Building2,
  Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const PromoterDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [showApplication, setShowApplication] = useState(false);

  // Check promoter status
  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ["promoterStatus"],
    queryFn: async () => {
      const res = await axios.get(`${API}/promoter/application/status`);
      return res.data;
    }
  });

  // Get dashboard stats (only for verified promoters)
  const { data: dashboardData, isLoading: dashLoading } = useQuery({
    queryKey: ["promoterDashboard"],
    queryFn: async () => {
      const res = await axios.get(`${API}/promoter/dashboard`);
      return res.data;
    },
    enabled: statusData?.status === "verified"
  });

  // Get promoter events
  const { data: eventsData } = useQuery({
    queryKey: ["promoterEvents"],
    queryFn: async () => {
      const res = await axios.get(`${API}/promoter/events`);
      return res.data;
    },
    enabled: statusData?.status === "verified"
  });

  const isVerified = statusData?.status === "verified";
  const isPending = statusData?.status === "pending";
  const isRejected = statusData?.status === "rejected";
  const notApplied = statusData?.status === "not_applied";

  if (statusLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not yet a promoter - show application
  if (notApplied || showApplication) {
    return <PromoterApplication user={user} onSuccess={() => { setShowApplication(false); refetchStatus(); }} />;
  }

  // Pending verification
  if (isPending) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Application Under Review</h1>
            <p className="text-muted-foreground">
              Your promoter application is being reviewed. We'll notify you once it's approved.
            </p>
          </div>
          
          <div className="glass-light rounded-2xl p-6">
            <h3 className="font-semibold mb-4">Application Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business Name</span>
                <span>{statusData?.business_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Submitted</span>
                <span>{new Date(statusData?.submitted_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="text-amber-400 capitalize">{statusData?.status}</span>
              </div>
            </div>
          </div>
        </div>
        <NavBar active="lifestyle" />
      </div>
    );
  }

  // Rejected
  if (isRejected) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Application Not Approved</h1>
            <p className="text-muted-foreground">
              Unfortunately, your application was not approved at this time.
            </p>
          </div>
          
          {statusData?.rejection_reason && (
            <div className="glass-light rounded-2xl p-6 mb-6 border-l-4 border-red-500">
              <h3 className="font-semibold mb-2">Reason</h3>
              <p className="text-sm text-muted-foreground">{statusData.rejection_reason}</p>
            </div>
          )}
          
          <Button 
            onClick={() => setShowApplication(true)}
            className="w-full btn-primary"
          >
            Apply Again
          </Button>
        </div>
        <NavBar active="lifestyle" />
      </div>
    );
  }

  // Verified Promoter Dashboard
  const stats = dashboardData?.stats || {};
  const events = eventsData?.events || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs text-muted-foreground">Promoter Dashboard</p>
            <h1 className="text-xl font-bold">{dashboardData?.promoter?.business_name}</h1>
          </div>
          <div className="flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs">
            <Shield className="w-3 h-3" />
            Verified
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard 
            icon={CalendarPlus} 
            label="Total Events" 
            value={stats.total_events || 0}
            color="text-purple-400"
          />
          <StatCard 
            icon={Ticket} 
            label="Tickets Sold" 
            value={stats.total_tickets_sold || 0}
            color="text-cyan-400"
          />
          <StatCard 
            icon={DollarSign} 
            label="Total Revenue" 
            value={`J$${(stats.total_revenue || 0).toLocaleString()}`}
            color="text-emerald-400"
          />
          <StatCard 
            icon={Clock} 
            label="Pending Review" 
            value={stats.pending_review || 0}
            color="text-amber-400"
          />
        </div>

        {/* Create Event Button */}
        <Button 
          onClick={() => navigate("/promoter/events/create")}
          className="w-full btn-primary mb-6 flex items-center justify-center gap-2"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4" />
          Create New Event
        </Button>

        {/* Events List */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Your Events</h2>
            {events.length > 3 && (
              <button 
                onClick={() => navigate("/promoter/events")}
                className="text-xs text-pink-400 flex items-center gap-1"
              >
                View All <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {events.length === 0 ? (
            <div className="glass-light rounded-2xl p-8 text-center">
              <CalendarPlus className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No events yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create your first event to start selling tickets</p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.slice(0, 5).map((event) => (
                <EventCard key={event.event_id} event={event} onClick={() => navigate(`/promoter/events/${event.event_id}`)} />
              ))}
            </div>
          )}
        </div>
      </div>
      <NavBar active="lifestyle" />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass-light rounded-xl p-4">
    <Icon className={`w-5 h-5 ${color} mb-2`} />
    <p className="text-lg font-bold">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const EventCard = ({ event, onClick }) => {
  const statusColors = {
    draft: "bg-zinc-500/20 text-zinc-400",
    pending_review: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    live: "bg-emerald-500/20 text-emerald-400",
    rejected: "bg-red-500/20 text-red-400",
    ended: "bg-zinc-500/20 text-zinc-400"
  };

  const status = event.status || (event.is_active ? "live" : "draft");
  
  return (
    <button 
      onClick={onClick}
      className="w-full glass-light rounded-xl p-4 flex items-center gap-4 text-left hover:bg-white/10 transition-all"
      data-testid={`event-card-${event.event_id}`}
    >
      {event.flyer_url ? (
        <img src={event.flyer_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center">
          <CalendarPlus className="w-6 h-6 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{event.name}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(event.event_date).toLocaleDateString()} • {event.venue}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusColors[status]}`}>
            {status.replace("_", " ")}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {event.total_sold || 0} sold • J${(event.total_revenue || 0).toLocaleString()}
          </span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
};

// Promoter Application Form
const PromoterApplication = ({ user, onSuccess }) => {
  const [form, setForm] = useState({
    business_name: "",
    business_type: "individual",
    contact_phone: "",
    business_address: "",
    parish: "",
    tax_id: "",
    website: "",
    social_media: "",
    experience_description: "",
    expected_events_per_year: 1
  });

  const parishes = [
    "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
    "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
    "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
  ];

  const applyMutation = useMutation({
    mutationFn: async (data) => {
      const res = await axios.post(`${API}/promoter/apply`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Application submitted successfully!");
      onSuccess();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    applyMutation.mutate(form);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24">
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center mb-3">
            <Building2 className="w-7 h-7 text-pink-400" />
          </div>
          <h1 className="text-xl font-bold">Become a Promoter</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Apply to list and sell tickets for your events on Stelx Pay
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="glass-light rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-sm">Business Information</h3>
            
            <div>
              <label className="text-xs text-muted-foreground">Business Name *</label>
              <input
                type="text"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Business Type *</label>
              <select
                value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
                <option value="organization">Organization</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Contact Phone *</label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Business Address *</label>
              <input
                type="text"
                value={form.business_address}
                onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Parish *</label>
              <select
                value={form.parish}
                onChange={(e) => setForm({ ...form, parish: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              >
                <option value="">Select Parish</option>
                {parishes.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Tax Registration Number</label>
              <input
                type="text"
                value={form.tax_id}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="glass-light rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-sm">Online Presence</h3>

            <div>
              <label className="text-xs text-muted-foreground">Website</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                placeholder="https://"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Social Media</label>
              <input
                type="text"
                value={form.social_media}
                onChange={(e) => setForm({ ...form, social_media: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                placeholder="@handle or URL"
              />
            </div>
          </div>

          <div className="glass-light rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-sm">Experience</h3>

            <div>
              <label className="text-xs text-muted-foreground">Tell us about your event experience *</label>
              <textarea
                value={form.experience_description}
                onChange={(e) => setForm({ ...form, experience_description: e.target.value })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                placeholder="Describe your experience hosting or promoting events..."
                required
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Expected Events Per Year *</label>
              <select
                value={form.expected_events_per_year}
                onChange={(e) => setForm({ ...form, expected_events_per_year: parseInt(e.target.value) })}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value={1}>1-2 events</option>
                <option value={5}>3-5 events</option>
                <option value={10}>6-10 events</option>
                <option value={20}>10+ events</option>
              </select>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary"
            disabled={applyMutation.isPending}
          >
            {applyMutation.isPending ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </div>
      <NavBar active="lifestyle" />
    </div>
  );
};

export default PromoterDashboard;
